// Shared poll-placeholder handling for anywhere a post's rich HTML gets
// rendered: the full post body (RichBodyWithCarousels) and preview-card
// teasers built from `custom_intro` (CardIntro). Both need to find the
// placeholder div the rich-text editor's PollNode leaves in saved HTML
// and swap it for the real <InteractivePoll> widget — an inert
// placeholder div has no visual content of its own, so skipping this
// step (as CardIntro originally did) makes an embedded poll render as
// nothing at all.
//
// Uses lookaheads for `class="poll-placeholder"` and `data-poll-id="..."`
// so it matches regardless of attribute order — TipTap's `mergeAttributes`
// actually serializes `data-poll-id` BEFORE `class` in the output HTML,
// which a naive class-then-id regex misses entirely.
export const POLL_RE = /<div\b(?=[^>]*\bclass="poll-placeholder")(?=[^>]*\bdata-poll-id="([a-zA-Z0-9-]+)")[^>]*><\/div>/gi;

/** True if `html` contains at least one poll placeholder. Resets
 * POLL_RE's lastIndex so repeat calls on different strings don't skip
 * matches (it's a shared `g` regex). */
export function htmlHasPoll(html) {
  if (!html) return false;
  POLL_RE.lastIndex = 0;
  const found = POLL_RE.test(html);
  POLL_RE.lastIndex = 0;
  return found;
}

/** Splits `html` into `{type:"html", value}` / `{type:"poll", id}`
 * segments at each poll placeholder, so the caller can render the
 * "html" segments verbatim (sanitized) and mount a real `<InteractivePoll
 * pollId={id}/>` for each "poll" segment. */
export function splitHtmlByPolls(html) {
  if (!html) return [];
  POLL_RE.lastIndex = 0;
  const segments = [];
  let cursor = 0;
  for (const match of html.matchAll(POLL_RE)) {
    const start = match.index ?? 0;
    if (start > cursor) segments.push({ type: "html", value: html.slice(cursor, start) });
    segments.push({ type: "poll", id: match[1] });
    cursor = start + match[0].length;
  }
  if (cursor < html.length) segments.push({ type: "html", value: html.slice(cursor) });
  return segments;
}
